/**
 * Instagram API Integration Module
 *
 * Fetches Instagram data through multiple RapidAPI providers with automatic
 * failover. Each provider has its own RapidAPI host and key routing — see
 * src/shared/libs/rapidapi-keys.ts for the key subscription matrix.
 *
 * PROVIDER CHAIN (in failover order):
 *   1. StarAPI       — PRIMARY. Full feature set. Uses KEY_D221.
 *   2. UpleapAPI     — Fallback #1. Profile-only. (Legacy, limited.)
 *   3. IGFlashAPI    — Fallback #2. Profile + similar + followers. Uses KEY_D221.
 *
 * All providers now use KEY_D221 via per-host routing in rapidapi-keys.ts.
 * The 511F RapidAPI account was lost on 2026-02-27.
 *
 * KEY FEATURES:
 *   - Redis caching with configurable TTLs per query type
 *   - Automatic provider failover with configurable retry count
 *   - Instagram CDN image proxying (Cloudflare Workers edge proxy)
 */

import urlJoin from "url-join"
import { redisClient } from "../redis"
import StarAPI from "./providers/StarAPI"
import UpleapAPI from "./providers/UpleapAPI"
import IGFlashAPI from "./providers/IGFlashAPI"

/**
 * Legacy re-exports for backwards compatibility.
 * These resolve to KEY_D221 (the StarAPI key) and are NOT used by any provider.
 * All API calls go through makeRapidApiRequest() which selects the correct
 * key per-host. These exports exist only to avoid breaking barrel imports.
 */
export { PRIMARY_RAPIDAPI_KEY as STARAPI_KEY, PRIMARY_RAPIDAPI_KEY as RAPIDAPI_KEY } from "../rapidapi-keys"

export const IG_QUERY_TYPES = {
    profile: "profile",
    users: "users",
    hashtags: "hashtags",
    places: "places",
    similarUsers: "similarUsers",
    userFollowers: "userFollowers",
    content_stories: "content_stories",
    content_posts: "content_posts",
    content_highlights: "content_highlights"
} as const
export type IGQueryTypes = (typeof IG_QUERY_TYPES)[keyof typeof IG_QUERY_TYPES]

const INSTAGRAM_AVATAR_HOST_SUFFIXES = ["cdninstagram.com", "fbcdn.net"] as const
const INSTAGRAM_REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308])
const MAX_INSTAGRAM_AVATAR_REDIRECTS = 3

function parseHttpsUrl(value: unknown): URL | null {
    if (typeof value !== "string" || !value.trim()) return null

    try {
        const parsed = new URL(value)
        if (parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.port) return null
        return parsed
    } catch {
        return null
    }
}

export function isAllowedInstagramAvatarUrl(value: unknown): value is string {
    const parsed = parseHttpsUrl(value)
    if (!parsed) return false

    const hostname = parsed.hostname.toLowerCase()
    return INSTAGRAM_AVATAR_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`))
}

function isAllowedInstagramAvatarFetchUrl(value: unknown): boolean {
    if (isAllowedInstagramAvatarUrl(value)) return true

    const parsed = parseHttpsUrl(value)
    return parsed?.hostname.toLowerCase() === "www.outfame.com" && parsed.pathname === "/ig-assets/"
}

async function fetchInstagramAvatarWithSafeRedirects(sourceUrl: string): Promise<Response | null> {
    let currentUrl = sourceUrl

    for (let redirectCount = 0; redirectCount <= MAX_INSTAGRAM_AVATAR_REDIRECTS; redirectCount += 1) {
        const response = await fetch(currentUrl, { redirect: "manual" })

        if (response.url && !isAllowedInstagramAvatarFetchUrl(response.url)) {
            await response.body?.cancel().catch(() => undefined)
            return null
        }

        if (!INSTAGRAM_REDIRECT_STATUSES.has(response.status)) return response
        if (redirectCount === MAX_INSTAGRAM_AVATAR_REDIRECTS) {
            await response.body?.cancel().catch(() => undefined)
            return null
        }

        const location = response.headers.get("location")
        if (!location) {
            await response.body?.cancel().catch(() => undefined)
            return null
        }

        const nextUrl = new URL(location, currentUrl).toString()
        if (!isAllowedInstagramAvatarFetchUrl(nextUrl)) {
            await response.body?.cancel().catch(() => undefined)
            return null
        }
        currentUrl = nextUrl
    }

    return null
}

export type InstagramProfileProps =
    | {
          graphql: {
              user: {
                  id: string
                  username: string
                  full_name?: string
                  biography?: string
                  category?: string
                  external_url?: string
                  external_url_title?: string
                  profile_pic_url?: string
                  profile_pic_url_hd?: string
                  follower_count?: number
                  following_count?: number
                  media_count?: number
                  is_private?: boolean
                  is_verified?: boolean
                  is_business?: boolean
                  is_professional?: boolean
                  /** Set by a provider that served an old cached copy (UpleapAPI). Never store its counts as today's reading. */
                  stale?: boolean
                  edge_followed_by?: any
                  edge_follow?: any
                  edge_owner_to_timeline_media?: Array<InstagramPostProps>
              }
          }
      }
    | false

export type InstagramProfileFetchOptions = {
    /** Numeric Instagram ID (accounts.platform_id). Lets StarAPI fall back to the by-ID endpoint when the username endpoint fails (business accounts). */
    userId?: string
    /** Treat a stale cached provider answer as a failure so the chain moves on instead of returning an old count. */
    rejectStale?: boolean
}

export type InstagramStoryProps = {
    id: string
    taken_at: number
    media_type: number
    shortcode: string
    image_url: string
    video_url: string
    video_view_count: number
    accessibility_caption?: string
    // is_paid_partnership?: boolean
}

export type InstagramHighlightProps = {
    id: string
    title: string
    cover_media_url: string
    items: Array<any>
}

export type InstagramHashtagQueryProps = {
    name: string
    target_id: string
    mediaCount?: number
    search_result_subtitle?: string
}

export type InstagramPlaceQueryProps = {
    target_id: string
    address: string
}

export type InstagramUserQueryProps = {
    full_name: string
    avatar_url: string
    profile_pic_url: string
    username: string
    is_verified: boolean
    is_private: boolean
    link: string
    type: string
    user_id: string
    follower_count?: number
}

export type InstagramQueryProps = InstagramHashtagQueryProps | InstagramPlaceQueryProps | InstagramUserQueryProps

export type InstagramPostProps = {
    id: string
    caption: string
    location?: string
    image_url: string
    hd_image_url: string
    like_count: number
    comment_count: number
    video_view_count: number
    taken_at: number
    accessibility_caption?: string
    is_video?: boolean
    video_url?: boolean
    is_album?: boolean
    album_children?: Array<InstagramAlbumChildPostProps>
}

export type InstagramAlbumChildPostProps = {
    id: string
    image_url: string
    is_video?: boolean
    video_url?: boolean
    video_view_count?: number
    accessibility_caption?: string
}

export type InstagramAnalyticsProps = {
    engagement_rate: number
    follower_to_following_ratio: number
    avg_likes_per_post: number
    avg_comments_per_post: number
    like_to_followers_ratio: number
    like_to_comment_ratio: number
    comment_to_followers_ratio: number
    best_time_to_post: string
    worst_time_to_post: string
    most_successful_post: string
    least_successful_post: string
    top_5_interests: {
        [key: string]: number
    }
    top_5_countries: {
        [key: string]: number
    }
    top_5_traffic_sources: {
        [key: string]: number
    }
    age_groups: {
        [key: string]: number
    }
    categories: string[]
}

export interface FetchingStrategy {
    strategyName: StrategyName
    fetchProfile(username: string, includePosts: boolean, options?: InstagramProfileFetchOptions): Promise<any>
    fetchUsernameByInstagramID(instagramId: string): Promise<string | boolean>
    fetchInstagramQuery(query: string, queryType: IGQueryTypes, limit?: number, attemptNumber?: number, backup?: boolean): Promise<any>
    fetchSimilarInstagramAccounts(instagramId: string, limit?: number, attemptNumber?: number, backup?: boolean): Promise<any>
    fetchUserFollowers(userInstagramId: string, limit?: number): Promise<boolean | InstagramProfileProps | [InstagramQueryProps] | { error: string }>
    fetchPostsByInstagramID?(instagramId: string): Promise<{ profile: InstagramProfileProps; posts: InstagramPostProps[] }>
    fetchStoriesByInstagramID?(instagramId: string): Promise<{ profile: InstagramProfileProps; stories: InstagramStoryProps[] }>
    fetchHighlightsByInstagramID?(instagramId: string): Promise<{ profile: InstagramProfileProps; highlights: InstagramHighlightProps[] }>
    processResponse(caller: string, response: any, queryType?: string, includePosts?: boolean): Promise<any>
}

export type StrategyName = "StarAPI" | "IGFlashAPI" | "UpleapAPI"

/**
 * Provider failover chain.
 * Order matters — first provider is tried first, then next on failure.
 *
 * Note: Not all providers support all operations. Some methods (e.g.,
 * fetchInstagramQuery) skip IGFlashAPI since it doesn't implement search.
 * Content fetching (stories/highlights/posts) is StarAPI-only.
 */
const strategies = [
    new StarAPI(), // Primary — full feature set, uses KEY_D221 via starapi1.p.rapidapi.com
    new UpleapAPI(), // Fallback 1 — profile-only (legacy)
    new IGFlashAPI() // Fallback 2 — profile + similar + followers, uses KEY_D221 via flashapi1.p.rapidapi.com
]

async function cacheOrFetch(redisKey: string, queryType: IGQueryTypes, ignoreCache = false, fetchFunction: () => Promise<any>): Promise<any> {
    let data: any
    if (!ignoreCache) {
        const cachedData = await redisClient.get(redisKey)
        if (cachedData) {
            // For content types, verify the specific content exists
            if (queryType.startsWith("content_")) {
                try {
                    const contentType = queryType.split("_")[1] // stories/posts/highlights
                    console.log(`🔎 Cached data: ${JSON.stringify(cachedData)}`)
                    if (cachedData && cachedData[contentType] && Array.isArray(cachedData[contentType])) {
                        return cachedData
                    }
                    // Content type not found in cache, need to fetch
                } catch (error) {
                    console.error("Error parsing cached data:", error)
                    // Continue to fetch new data
                }
            } else if (queryType === IG_QUERY_TYPES.profile && (await hasNoExpiry(redisKey))) {
                // Entries written before 2026-02-14 have no expiry and would serve the same counts forever: refetch, which rewrites the key with a TTL
                console.warn(`[cacheOrFetch] ${redisKey} has no TTL (legacy entry), refetching`)
            } else {
                return cachedData
            }
        }
    }
    data = await fetchFunction()
    if (typeof data === "boolean") return data // If data is of type boolean, it means that the query returned no results
    if (!data) {
        console.warn(`[cacheOrFetch] No data fetched for query: ${redisKey}`)
        return false
    }

    // Set expiration based on content type
    let expiration: number
    if (queryType.startsWith("content_")) {
        // 1 hour for posts, stories, highlights
        expiration = 60 * 60
    } else if (queryType === IG_QUERY_TYPES.users) {
        // 24 hours for user search results
        expiration = 24 * 60 * 60
    } else if (queryType === IG_QUERY_TYPES.profile) {
        // 24 hours for profiles - balances freshness with cost
        expiration = 24 * 60 * 60
    } else if (queryType === IG_QUERY_TYPES.hashtags || queryType === IG_QUERY_TYPES.places) {
        // 30 days for hashtags and places
        expiration = 30 * 24 * 60 * 60
    } else {
        // 7 days for all other unhandled types
        expiration = 7 * 24 * 60 * 60
    }

    // A stale provider copy must not be cached: the daily job reads this key and would store the old count
    if (queryType === IG_QUERY_TYPES.profile && data?.graphql?.user?.stale) return data

    try {
        await redisClient.setex(redisKey, expiration, JSON.stringify(data))
    } catch (error) {
        console.error("Error saving data in Redis: ", error)
    }
    return data
}

async function hasNoExpiry(redisKey: string): Promise<boolean> {
    try {
        return (await redisClient.ttl(redisKey)) === -1
    } catch {
        return false
    }
}

function getNextStrategy(strategyIndex: number): { strategy: FetchingStrategy; nextIndex: number } {
    const nextIndex = (strategyIndex + 1) % strategies.length
    return { strategy: strategies[nextIndex], nextIndex }
}

async function fetchData(config: { redisKey?: string; maxRetries: number; fetchFunction: (strategy: FetchingStrategy) => Promise<any>; preferredStrategy?: FetchingStrategy; forcePreferredStrategy?: boolean }): Promise<any> {
    let attemptNumber = 0
    let strategyIndex = -1
    const errors: any[] = []
    while (attemptNumber < config.maxRetries) {
        let strategy
        if (attemptNumber === 0 && config.preferredStrategy) {
            strategy = config.preferredStrategy
        } else if (!config.forcePreferredStrategy) {
            const { strategy: nextStrategy, nextIndex } = getNextStrategy(strategyIndex)
            strategy = nextStrategy
            strategyIndex = nextIndex
        }
        try {
            return await config.fetchFunction(strategy)
        } catch (error) {
            const isSkip = (error as any).message?.includes("Skipping")
            if (!isSkip) console.log(error) // Silent skipping strategy errors
            errors.push(`Strategy ${strategy?.strategyName}: ${error.message}`)
            // Always count real failures as attempts; skip errors don't count
            if (!isSkip) attemptNumber += 1
        }
    }
    throw new Error(`Failed to fetch data after ${config.maxRetries} retries with all strategies. Errors: ${errors.join("; ")}`)
}

export class Instagram {
    public static async fetchInstagramProfile(username: string, includePosts = false, ignoreCache = false, preferredStrategy: FetchingStrategy = null, maxRetries = 3, forcePreferredStrategy = false, options: InstagramProfileFetchOptions = {}): Promise<InstagramProfileProps | any> {
        // if includePosts is true, the profile will include post data
        const redisKey = `ig_profile_${includePosts ? "full_" : ""}${username.replaceAll(" ", "")}`
        console.log(`[fetchInstagramProfile] redisKey: ${redisKey} includePosts: ${includePosts} ignoreCache: ${ignoreCache} preferredStrategy: ${preferredStrategy} forcePreferredStrategy: ${forcePreferredStrategy}`)
        return await cacheOrFetch(redisKey, IG_QUERY_TYPES.profile, ignoreCache, async () => {
            return await fetchData({
                redisKey,
                maxRetries,
                preferredStrategy,
                forcePreferredStrategy,
                fetchFunction: async (strategy: FetchingStrategy) => {
                    console.log(`🔎 Fetching IG profile '${username}' on attempt #${strategies.indexOf(strategy) + 1} (${strategy.strategyName})...`)
                    console.log(`Include posts: ${includePosts}`)
                    console.log(`Force preferred strategy: ${forcePreferredStrategy}, strategy: ${preferredStrategy ? preferredStrategy.strategyName : "null"}`)
                    return await strategy.fetchProfile(username, includePosts, options)
                }
            })
        })
    }

    // fetchUsernameByInstagramID is supported by StarAPI only
    public static async fetchUsernameByInstagramID(instagramID: string): Promise<string | boolean> {
        return await fetchData({
            maxRetries: 3,
            fetchFunction: async (strategy: FetchingStrategy) => {
                if (strategy.strategyName === "StarAPI") {
                    console.log(`🔎 Fetching IG username for ID '${instagramID}' using StarAPI...`)
                    return await strategy.fetchUsernameByInstagramID(instagramID)
                }
                throw new Error(`🔵 Skipping ${strategy.strategyName} for fetchUsernameByInstagramID as it is not supported.`)
            }
        })
    }

    public static async fetchInstagramQuery(query: string, queryType: IGQueryTypes, limit = 15, preferredStrategy: FetchingStrategy = null, ignoreCache = false, maxRetries = 3, backup = false, forcePreferredStrategy = false): Promise<[InstagramQueryProps] | { error: string }> {
        if (!IG_QUERY_TYPES[queryType]) return { error: `Invalid query type: ${queryType}` }

        const redisKey = `ig_query_${queryType}_${query}`
        return await cacheOrFetch(redisKey, queryType, ignoreCache, async () => {
            return await fetchData({
                redisKey,
                maxRetries,
                preferredStrategy,
                forcePreferredStrategy,
                fetchFunction: async (strategy: FetchingStrategy) => {
                    // Skip UpleapAPI for hashtags/places (broken on Upleap server — returns 500)
                    if ((queryType === "hashtags" || queryType === "places") && strategy.strategyName === "UpleapAPI") {
                        throw new Error("Skipping UpleapAPI for hashtags/places (broken on Upleap server)")
                    }
                    // Skip IGFlashAPI for fetchInstagramQuery as it's not supported
                    if (strategy.strategyName === "IGFlashAPI") {
                        console.log("Skipping IGFlashAPI for fetchInstagramQuery as it is not supported.")
                        throw new Error("Skipping IGFlashAPI")
                    }
                    console.log(`🔎 Fetching ${queryType ? `${queryType}` : ""} IG query '${query}' on attempt #${strategies.indexOf(strategy) + 1} (${strategy.strategyName})...`)
                    return await strategy.fetchInstagramQuery(query, queryType, limit, maxRetries, backup)
                }
            })
        })
    }

    public static async fetchSimilarInstagramAccounts(instagramId: string, limit = 15, preferredStrategy: FetchingStrategy = null, ignoreCache = false, maxRetries = 3, backup = false, forcePreferredStrategy = false): Promise<[InstagramUserQueryProps] | { error: string }> {
        const redisKey = `ig_similar_for_${instagramId}`
        return await cacheOrFetch(redisKey, IG_QUERY_TYPES.users, ignoreCache, async () => {
            return await fetchData({
                redisKey,
                maxRetries,
                preferredStrategy,
                forcePreferredStrategy,
                fetchFunction: async (strategy: FetchingStrategy) => {
                    // StarAPI and IGFlashAPI support fetchSimilarInstagramAccounts
                    if (strategy.strategyName !== "StarAPI" && strategy.strategyName !== "IGFlashAPI") {
                        console.log(`🔵 Skipping ${strategy.strategyName} for fetchSimilarInstagramAccounts as it is not supported.`)
                        throw new Error(`🔵 Skipping ${strategy.strategyName} for fetchSimilarInstagramAccounts as it is not supported.`)
                    }
                    console.log(`🔎 Fetching similar IG accounts for '${instagramId}' on attempt #${strategies.indexOf(strategy) + 1} (${strategy.strategyName})...`)
                    return await strategy.fetchSimilarInstagramAccounts(instagramId, limit, maxRetries, backup)
                }
            })
        })
    }

    private static async fetchInstagramContent(fetchType: "stories" | "highlights" | "posts", instagramId: string): Promise<{ profile: InstagramProfileProps; stories?: InstagramStoryProps[]; highlights?: InstagramHighlightProps[]; posts?: InstagramPostProps[] }> {
        const redisKey = `ig_content_${instagramId}`

        return await cacheOrFetch(redisKey, `content_${fetchType}`, false, async () => {
            // Try to get existing content data if it exists
            const existingData = (await redisClient.get(redisKey)) as { profile: InstagramProfileProps; stories?: InstagramStoryProps[]; highlights?: InstagramHighlightProps[]; posts?: InstagramPostProps[] }

            const newData = await fetchData({
                redisKey,
                maxRetries: 3,
                preferredStrategy: strategies.find((s) => s.strategyName === "StarAPI"),
                forcePreferredStrategy: false,
                fetchFunction: async (strategy: FetchingStrategy) => {
                    // Only StarAPI supports content fetching
                    if (strategy.strategyName !== "StarAPI") {
                        console.log(`Skipping ${strategy.strategyName} for fetch${fetchType}ByInstagramID as it is only supported by StarAPI.`)
                        throw new Error(`fetch${fetchType}ByInstagramID is only supported by StarAPI.`)
                    }
                    console.log(`🔎 Fetching IG ${fetchType} for '${instagramId}' using ${strategy.strategyName}...`)

                    let newContentData
                    switch (fetchType) {
                        case "stories":
                            newContentData = await strategy.fetchStoriesByInstagramID(instagramId)
                            break
                        case "highlights":
                            newContentData = await strategy.fetchHighlightsByInstagramID(instagramId)
                            break
                        case "posts":
                            newContentData = await strategy.fetchPostsByInstagramID(instagramId)
                            break
                        default:
                            throw new Error(`Invalid fetchType: ${fetchType}`)
                    }

                    // Merge data, keeping existing profile if new one is missing
                    const mergedData = {
                        reportUpdatedAt: new Date().toISOString(),
                        ...(existingData && typeof existingData === "object" ? existingData : {}),
                        profile: existingData?.profile
                            ? {
                                  ...existingData.profile,
                                  ...(newContentData?.profile && Object.fromEntries(Object.entries(newContentData.profile).filter(([_, value]) => value != null)))
                              }
                            : newContentData?.profile || {},
                        [fetchType]: newContentData[fetchType] || [] // Use empty array if no content
                    }

                    return mergedData
                }
            })

            // Update cache with merged data
            await redisClient.setex(redisKey, 60 * 60, JSON.stringify(newData))
            return newData
        })
    }

    public static async fetchStoriesByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; stories: InstagramStoryProps[] }> {
        const result = await this.fetchInstagramContent("stories", instagramId)
        return { profile: result.profile, stories: result.stories as InstagramStoryProps[] }
    }

    public static async fetchHighlightsByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; highlights: InstagramHighlightProps[] }> {
        const result = await this.fetchInstagramContent("highlights", instagramId)
        return { profile: result.profile, highlights: result.highlights as InstagramHighlightProps[] }
    }

    public static async fetchPostsByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; posts: InstagramPostProps[] }> {
        const result = await this.fetchInstagramContent("posts", instagramId)
        return { profile: result.profile, posts: result.posts as InstagramPostProps[] }
    }

    public static async downloadInstagramAvatar(username: string, avatarUrl: string, supabaseClient: any, bucket: string = "avatars", folder: string = ""): Promise<string | null> {
        try {
            if (!isAllowedInstagramAvatarUrl(avatarUrl)) {
                console.warn(`Rejected non-Instagram avatar URL for '${username}'.`)
                return null
            }

            // Use proxy URL to avoid 403 errors from Instagram CDN blocking server-side requests
            const proxiedUrl = this.getProxiedImageUrl(avatarUrl)
            const candidateUrls = Array.from(new Set([proxiedUrl, avatarUrl].filter((url): url is string => Boolean(url) && isAllowedInstagramAvatarFetchUrl(url))))
            console.log(`🔎 Downloading IG avatar for '${username}' via proxy...`)

            let response: Response | null = null
            for (const candidateUrl of candidateUrls) {
                response = await fetchInstagramAvatarWithSafeRedirects(candidateUrl)
                if (response?.ok) break
                await response?.body?.cancel().catch(() => undefined)
            }

            if (!response?.ok) {
                console.warn(`Failed to fetch IG avatar for '${username}'.`)
                return null
            }

            const fileData = await response.arrayBuffer()
            const imagePath = folder ? `${folder}/${username}.jpg` : `${username}.jpg`
            const supabaseFileUrl = urlJoin(process.env.NEXT_PUBLIC_SUPABASE_URL!, `/storage/v1/object/public/${bucket}`, imagePath)
            const { error } = await supabaseClient.storage.from(bucket).upload(imagePath, fileData, {
                upsert: true,
                contentType: "image/jpeg",
                cacheControl: "31536000" // 1 year browser cache - reduces egress costs
            })
            if (error) {
                if (error.message.includes("The resource already exists")) return supabaseFileUrl
                console.warn(`Error uploading IG avatar for '${username}': ${error.message}`)
                return null
            }
            return supabaseFileUrl
        } catch (error) {
            console.warn(`[downloadInstagramAvatar] Non-fatal error for '${username}': ${error instanceof Error ? error.message : error}`)
            return null
        }
    }

    /**
     * Re-host an arbitrary Instagram CDN image to durable Supabase storage at an
     * explicit path. Mirrors downloadInstagramAvatar (proxy-fetch → upload, with a
     * direct-fetch fallback). Returns the public Supabase URL, or null on failure.
     */
    public static async downloadInstagramImage(sourceUrl: string, supabaseClient: any, bucket: string, path: string): Promise<string | null> {
        try {
            const proxiedUrl = this.getProxiedImageUrl(sourceUrl)
            let response = await fetch(proxiedUrl, { redirect: "follow" })
            if (!response.ok) {
                response = await fetch(sourceUrl, { redirect: "follow" })
                if (!response.ok) return null
            }
            const fileData = await response.arrayBuffer()
            const supabaseFileUrl = urlJoin(process.env.NEXT_PUBLIC_SUPABASE_URL!, `/storage/v1/object/public/${bucket}`, path)
            const { error } = await supabaseClient.storage.from(bucket).upload(path, fileData, {
                upsert: true,
                contentType: "image/jpeg",
                cacheControl: "31536000"
            })
            if (error && !String(error.message).includes("The resource already exists")) return null
            return supabaseFileUrl
        } catch {
            return null
        }
    }

    public static async fetchUserFollowers(userInstagramId: string, limit: number = 15, ignoreCache = false): Promise<any> {
        const redisKey = `ig_followers_for_${userInstagramId}`
        return await cacheOrFetch(redisKey, IG_QUERY_TYPES.userFollowers, ignoreCache, async () => {
            return await fetchData({
                redisKey,
                maxRetries: 3,
                fetchFunction: async (strategy: FetchingStrategy) => {
                    // StarAPI and IGFlashAPI support fetchUserFollowers
                    if (strategy.strategyName !== "StarAPI" && strategy.strategyName !== "IGFlashAPI") {
                        throw new Error(`🔵 Skipping ${strategy.strategyName} for fetchUserFollowers as it is not supported.`)
                    }
                    console.log(`🔎 Fetching IG followers for '${userInstagramId}' on attempt #${strategies.indexOf(strategy) + 1} (${strategy.strategyName})...`)
                    return await strategy.fetchUserFollowers(userInstagramId, limit)
                }
            })
        })
    }

    public static async fetchAllCachedInstagramProfiles(): Promise<any> {
        // Returns the usernames of all the cached Instagram profiles
        let cursor = 0
        let keys = []
        do {
            const res = await redisClient.scan(cursor, { match: "ig_profile_*", count: 1000 })
            cursor = parseInt(res[0], 10)
            keys.push(...res[1])
        } while (cursor !== 0)
        if (!keys || keys.length === 0) return []
        const usernames = keys.map((key) => key.replace("ig_profile_full_", "").replace("ig_profile_", ""))
        return [...new Set(usernames)] // Remove duplicates
    }

    public static async isInstagramProfileCached(username: string): Promise<boolean> {
        let cursor = 0
        do {
            const res = await redisClient.scan(cursor, { match: `ig_profile_*${username}`, count: 1000 })
            cursor = parseInt(res[0])
            const keys = res[1]
            if (keys.length > 0) return true
        } while (cursor !== 0)
        return false
    }

    /**
     * Cloudflare Worker edge proxy URL for Instagram CDN images.
     * Edge-based proxy provides ~10ms latency vs ~100ms+ for server-side proxy.
     */
    private static readonly EDGE_PROXY_URL = "https://www.outfame.com/ig-assets/"

    /**
     * Primary method: Use Cloudflare Workers edge proxy for optimal performance.
     * Edge proxy runs at 300+ locations worldwide, providing ~10ms latency.
     */
    public static getProxiedImageUrl(url: string, useApiProxy: boolean = true): string {
        if (!url || !url.includes("http")) return url

        // Already proxied - return as-is
        if (url.includes("/api/ig-proxy") || url.includes("outfame.com/ig-assets") || url.includes("instagram-asset-cdn.dragos-balan-pr.workers.dev")) {
            return url
        }

        // Our own re-hosted Supabase storage assets (avatars/posts) must never be re-proxied —
        // the isInstagramCdn substring heuristic below would wrongly wrap an own-host URL whose
        // storage path contains a handle like "x.instagram.y" or "scontent". This marker is
        // host-agnostic (works for *.supabase.co and the db.outfame.com custom domain alike).
        if (url.includes("/storage/v1/object/public/")) {
            return url
        }

        // Check if it's an Instagram CDN URL that needs proxying
        const isInstagramCdn = url.includes("cdninstagram.com") || url.includes("fbcdn.net") || url.includes("instagram.") || url.includes("scontent")

        if (!isInstagramCdn) return url

        // Primary: Use Cloudflare Workers edge proxy (fastest, global edge network)
        if (useApiProxy) {
            return `${this.EDGE_PROXY_URL}?url=${encodeURIComponent(url)}`
        }

        // Fallback: Use Google Translate proxy (legacy method)
        return this.decodeInstagramMediaURLLegacy(url)
    }

    /**
     * Legacy method: Google Translate proxy (kept as fallback)
     * Warning: Subject to rate limiting (429 errors)
     */
    public static decodeInstagramMediaURLLegacy(url: string): string {
        if (!url.includes("http")) return url
        if (url.includes(atob("LnRyYW5zbGF0ZS5nb29n"))) return url
        const p = url.split("/")
        let t = ""
        for (let i = 0; i < p.length; i++) t += i == 2 ? p[i].replaceAll("-", "--").replaceAll(".", "-") + atob("LnRyYW5zbGF0ZS5nb29n") + "/" : i != p.length - 1 ? p[i] + "/" : p[i]
        return t
    }

    /**
     * Main entry point for proxying Instagram images
     * Now uses API proxy by default instead of Google Translate
     */
    public static decodeInstagramMediaURL(url: string): string {
        return this.getProxiedImageUrl(url, true)
    }

    public static decodeInstagramURLtoSelfHosted(url: string): string {
        // Extract filename using regex pattern matching for both URL types
        const filename = url.includes("instagram.fmad") ? url.split("/").pop()?.split("?")[0] : url.match(/\/([^\/]+?\.(jpg|mp4|webp))/i)?.[1]

        if (!filename) return url

        // Build self-hosted URL with parameters
        return `https://www.outfame.com/ig-assets/${filename}?url=${encodeURIComponent(url)}&time=${Math.floor(Date.now() / 1000 + 86400)}`
    }

    public static graphqlIDToShortcode(graphqlID: string): string {
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_"
        let id = BigInt(graphqlID.split("_")[0]) // split by underscore and take the first part
        let shortcode = ""
        while (id > BigInt(0)) {
            let remainder = id % BigInt(64)
            id = id / BigInt(64)
            shortcode = alphabet.charAt(Number(remainder)) + shortcode
        }
        return shortcode
    }
}
